<template>
  <div class="ss-wrap" ref="wrapRef">
    <!-- Selected pill or placeholder input -->
    <div
      class="ss-control"
      :class="{ 'ss-control--open': open, 'ss-control--disabled': disabled }"
      @click="!disabled && openDropdown()"
    >
      <!-- Selected value -->
      <span v-if="selectedLabel" class="ss-selected-label">{{ selectedLabel }}</span>
      <span v-else class="ss-placeholder">{{ placeholder }}</span>

      <div class="ss-right">
        <!-- Clear -->
        <button v-if="modelValue && !disabled" class="ss-clear" @click.stop="clear" type="button" tabindex="-1">
          <font-awesome-icon icon="times" />
        </button>
        <font-awesome-icon icon="chevron-down" class="ss-chevron" :class="{ 'ss-chevron--open': open }" />
      </div>
    </div>

    <!-- Dropdown -->
    <div v-show="open" class="ss-dropdown">
      <!-- Search input -->
      <div class="ss-search-wrap">
        <font-awesome-icon icon="search" class="ss-search-icon" />
        <input
          ref="searchRef"
          class="ss-search"
          type="text"
          v-model="query"
          :placeholder="searchPlaceholder"
          @input="onInput"
          @keydown.down.prevent="moveDown"
          @keydown.up.prevent="moveUp"
          @keydown.enter.prevent="selectHighlighted"
          @keydown.esc="close"
        />
        <span v-if="loading" class="ss-spin"><font-awesome-icon icon="spinner" spin /></span>
      </div>

      <!-- Options -->
      <ul class="ss-options" ref="listRef">
        <li
          v-for="(opt, i) in options"
          :key="opt[valueKey]"
          class="ss-option"
          :class="{
            'ss-option--active': opt[valueKey] === modelValue,
            'ss-option--highlighted': i === highlightedIdx
          }"
          @mousedown.prevent="select(opt)"
          @mouseover="highlightedIdx = i"
        >
          <span class="ss-option-label">{{ opt[labelKey] }}</span>
          <span v-if="opt[sublabelKey]" class="ss-option-sub">{{ opt[sublabelKey] }}</span>
        </li>
        <li v-if="!loading && !options.length" class="ss-empty">
          {{ query.length >= minChars ? 'No results found' : `Type to search…` }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SearchSelect',
  emits: ['update:modelValue', 'select'],

  props: {
    modelValue:       { default: null },                   // bound ID value
    url:              { type: String, required: true },    // API endpoint
    queryParam:       { type: String, default: 'search' }, // query string key
    labelKey:         { type: String, default: 'name' },   // option display field
    sublabelKey:      { type: String, default: '' },       // secondary display field (optional)
    valueKey:         { type: String, default: 'id' },     // option value field
    placeholder:      { type: String, default: 'Select…' },
    searchPlaceholder:{ type: String, default: 'Type to search…' },
    minChars:         { type: Number, default: 0 },        // 0 = fetch on open
    extraParams:      { type: Object, default: () => ({}) },
    responseKey:      { type: String, default: '' },       // e.g. 'data' or 'data.data'
    disabled:         { type: Boolean, default: false },
    initialLabel:     { type: String, default: '' },       // label to show for pre-selected value
  },

  data() {
    return {
      open: false,
      query: '',
      options: [],
      loading: false,
      highlightedIdx: -1,
      debounceTimer: null,
      cachedLabel: this.initialLabel || '',
    };
  },

  computed: {
    selectedLabel() {
      if (!this.modelValue) return '';
      // Try to find in current options list first
      const found = this.options.find(o => o[this.valueKey] === this.modelValue);
      return found ? found[this.labelKey] : this.cachedLabel;
    },
  },

  watch: {
    modelValue(val) {
      if (!val) { this.cachedLabel = ''; return; }
      const found = this.options.find(o => o[this.valueKey] === val);
      if (found) this.cachedLabel = found[this.labelKey];
    },
    initialLabel(v) { this.cachedLabel = v; },
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    openDropdown() {
      this.open = true;
      this.query = '';
      this.highlightedIdx = -1;
      if (this.minChars === 0) this.fetchOptions('');
      this.$nextTick(() => this.$refs.searchRef?.focus());
    },

    close() {
      this.open = false;
      this.options = [];
      this.query = '';
      this.highlightedIdx = -1;
    },

    clear() {
      this.$emit('update:modelValue', null);
      this.$emit('select', null);
      this.cachedLabel = '';
      this.close();
    },

    onInput() {
      clearTimeout(this.debounceTimer);
      if (this.query.length < this.minChars) { this.options = []; return; }
      this.debounceTimer = setTimeout(() => this.fetchOptions(this.query), 260);
    },

    async fetchOptions(q) {
      this.loading = true;
      try {
        const params = { [this.queryParam]: q, length: 30, ...this.extraParams };
        const { data } = await axios.get(this.url, { params });
        // resolve nested key e.g. 'data.data' or 'data'
        let result = data;
        for (const seg of (this.responseKey || 'data').split('.')) {
          result = result?.[seg];
        }
        // Handle Laravel paginated response
        this.options = Array.isArray(result) ? result : (result?.data || []);
      } catch {
        this.options = [];
      } finally {
        this.loading = false;
      }
    },

    select(opt) {
      this.$emit('update:modelValue', opt[this.valueKey]);
      this.$emit('select', opt);
      this.cachedLabel = opt[this.labelKey];
      this.close();
    },

    moveDown() {
      if (this.highlightedIdx < this.options.length - 1) {
        this.highlightedIdx++;
        this.scrollToHighlighted();
      }
    },
    moveUp() {
      if (this.highlightedIdx > 0) {
        this.highlightedIdx--;
        this.scrollToHighlighted();
      }
    },
    selectHighlighted() {
      if (this.highlightedIdx >= 0 && this.options[this.highlightedIdx]) {
        this.select(this.options[this.highlightedIdx]);
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const list = this.$refs.listRef;
        const item = list?.children[this.highlightedIdx];
        item?.scrollIntoView({ block: 'nearest' });
      });
    },

    handleOutsideClick(e) {
      if (this.$refs.wrapRef && !this.$refs.wrapRef.contains(e.target)) this.close();
    },
  },
};
</script>

<style scoped>
.ss-wrap { position:relative; width:100%; }

.ss-control {
  display:flex; align-items:center; justify-content:space-between;
  min-height:38px; padding:0 10px; border:1px solid #dde5f0; border-radius:8px;
  background:#fff; cursor:pointer; user-select:none; gap:8px;
  transition:border-color .15s;
}
.ss-control:hover:not(.ss-control--disabled) { border-color:#aab8cc; }
.ss-control--open  { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.1); }
.ss-control--disabled { background:#f8fafc; cursor:not-allowed; opacity:.65; }

.ss-selected-label { font-size:13px; color:#1a2b45; flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ss-placeholder     { font-size:13px; color:#aab8cc; flex:1; }

.ss-right  { display:flex; align-items:center; gap:6px; flex-shrink:0; }
.ss-clear  { background:none; border:none; cursor:pointer; color:#aab8cc; padding:2px 4px; border-radius:4px; font-size:11px; line-height:1; }
.ss-clear:hover { color:#dc2626; }
.ss-chevron { font-size:10px; color:#7a90b0; transition:transform .2s; }
.ss-chevron--open { transform:rotate(180deg); }

/* Dropdown */
.ss-dropdown {
  position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:1500;
  background:#fff; border:1px solid #e4eaf2; border-radius:10px;
  box-shadow:0 8px 30px rgba(0,0,0,.12); overflow:hidden;
}

.ss-search-wrap {
  display:flex; align-items:center; gap:8px;
  padding:8px 12px; border-bottom:1px solid #f0f4f8;
}
.ss-search-icon { color:#aab8cc; font-size:12px; flex-shrink:0; }
.ss-search {
  flex:1; border:none; outline:none; font-size:13px; color:#1a2b45;
  background:transparent;
}
.ss-search::placeholder { color:#aab8cc; }
.ss-spin { color:#aab8cc; font-size:12px; }

.ss-options { list-style:none; margin:0; padding:4px; max-height:220px; overflow-y:auto; }
.ss-option {
  display:flex; flex-direction:column; gap:1px;
  padding:8px 10px; border-radius:7px; cursor:pointer;
  transition:background .1s;
}
.ss-option:hover,
.ss-option--highlighted { background:#f0f7ff; }
.ss-option--active { background:#e0efff; }
.ss-option-label { font-size:13px; color:#1a2b45; font-weight:500; }
.ss-option-sub   { font-size:11px; color:#7a90b0; }

.ss-empty { padding:14px 10px; font-size:13px; color:#aab8cc; text-align:center; }
</style>
